import React, { useState, useEffect } from 'react';
import { Link, useLocation } from "react-router-dom";
import axios from 'axios';
import SEO from '../../../components/common/SEO';
import { formatDate } from '../../../utils/dateUtils';
import { formatPrice } from '../../../utils/priceUtils';
import confirmDeletion from '../../utils/confirmDeletion';

function ListingHotels() {
    const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
    const location = useLocation();
    const [vendor, setVendor] = useState(null);

    // State for API data
    const [hotels, setHotels] = useState([]);
    const [loading, setLoading] = useState(true);
    const [apiError, setApiError] = useState(null);

    const [successMessage, setSuccessMessage] = useState('');
    const [search, setSearch] = useState('');
    const [currentPage, setCurrentPage] = useState(1);

    const [sortConfig, setSortConfig] = useState({ key: 'id', direction: 'desc' });

    const pageSize = 20;

    useEffect(() => {
        const storedUserInfo = localStorage.getItem('userInfo');
        console.log("storedUserInfo", storedUserInfo);
        if (storedUserInfo) {
            const userData = JSON.parse(storedUserInfo);
            setVendor(userData);
        }
        const fetchHotels = async () => {
            setLoading(true);
            try {
                const token = localStorage.getItem('authToken');
                console.log("token", token);
                const response = await axios.get(`${API_BASE_URL}/hotels`, {
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                });


                console.log("response", response);
                setHotels(response.data);
                setApiError(null);
            } catch (error) {
                console.error('Error fetching hotels:', error);
                setApiError('Failed to fetch hotels. Please try again later.');
            } finally {
                setLoading(false);
            }
        };

        fetchHotels();
    }, [API_BASE_URL]);

    useEffect(() => {
        if (location.state && location.state.message) {
            setSuccessMessage(location.state.message);
            const timer = setTimeout(() => {
                setSuccessMessage('');
                window.history.replaceState({}, document.title);
            }, 5000);
            return () => clearTimeout(timer);
        }
    }, [location.state]);

    const handleSearchChange = (e) => {
        setSearch(e.target.value);
        setCurrentPage(1);
    };

    const getNestedValue = (obj, path) => {
        return path.split('.').reduce((acc, part) => acc && acc[part], obj);
    };

    const sortedData = [...hotels].sort((a, b) => {
        if (!sortConfig.key) return 0;
        const aValue = getNestedValue(a, sortConfig.key) || '';
        const bValue = getNestedValue(b, sortConfig.key) || '';
        if (typeof aValue === 'number' && typeof bValue === 'number') {
            return sortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue;
        }
        return sortConfig.direction === 'asc'
            ? String(aValue).localeCompare(String(bValue))
            : String(bValue).localeCompare(String(aValue));
    });

    const filteredData = sortedData.filter(hotel =>
        Object.values(hotel).some(value =>
            String(value).toLowerCase().includes(search.toLowerCase())
        )
    );

    const totalPages = Math.ceil(filteredData.length / pageSize);
    const paginatedData = filteredData.slice((currentPage - 1) * pageSize, currentPage * pageSize);

    const requestSort = (key) => {
        let direction = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({ key, direction });
    };

    const renderSortArrow = (key) => {
        if (sortConfig.key !== key) return '';
        return sortConfig.direction === 'asc' ? ' ▲' : ' ▼';
    };

    const handleDelete = async (hotelId) => {
        if (!(await confirmDeletion())) return;
        try {
            const token = localStorage.getItem('authToken');
            await axios.delete(`${API_BASE_URL}/hotels/${hotelId}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setHotels(prevHotels => prevHotels.filter(hotel => hotel.id !== hotelId));
            setSuccessMessage('Hotel deleted successfully!');
            setTimeout(() => setSuccessMessage(''), 5000);
        } catch (error) {
            console.error('Error deleting hotel:', error);
            alert('Failed to delete hotel.');
        }
        // }
    };

    const renderStatusBadge = (status, reason) => {
        let badgeClass = 'bg-secondary';
        if (status === 'Published') badgeClass = 'bg-success';
        else if (status === 'Under Review') badgeClass = 'bg-warning';
        else if (status === 'Rejected') badgeClass = 'bg-danger';

        return (
            <div>
                <span className={`badge ${badgeClass}`}>{status || 'Published'}</span>
                {status === 'Rejected' && reason && (
                    <div className="text-danger mt-1" style={{ fontSize: '11px', whiteSpace: 'normal', maxWidth: '150px' }}>
                        <i className="ri-error-warning-line align-middle me-1"></i>
                        {reason}
                    </div>
                )}
            </div>
        );
    };

    return (
        <div className="row">
            <SEO title="Hotels Management | Mangoloops" />
            {/* <div className="col-12">
                <div className="page-title-box d-sm-flex align-items-center justify-content-between">
                    <h4 className="mb-sm-0">Hotels</h4>
                    <div className="page-title-right">
                        <ol className="breadcrumb m-0">
                            <li className="breadcrumb-item"><a href="#">Dashboard</a></li>
                            <li className="breadcrumb-item active">Hotels</li>
                        </ol>
                    </div>
                </div>
            </div> */}

            <div className="col-lg-12">
                <div className="card">
                    <div className="card-header d-flex justify-content-between align-items-center flex-wrap gap-2">
                        <h4 className="card-title mb-0">Hotel Listing</h4>
                        <div className="d-flex flex-wrap gap-2">
                            <input
                                type="text"
                                className="form-control"
                                placeholder="🔍 Search Hotels..."
                                value={search}
                                onChange={handleSearchChange}
                                style={{ width: '250px' }}
                            />
                            <Link to="/admin/add-hotel" className="btn btn-soft-secondary">Add Hotel</Link>
                        </div>
                    </div>

                    {successMessage && <div className="alert alert-success m-3">{successMessage}</div>}
                    {apiError && <div className="alert alert-danger m-3">{apiError}</div>}

                    <div className="card-body">
                        <div className="table-responsive">
                            <table className="table table-bordered table-hover">
                                <thead className="table-light">
                                    <tr>
                                        <th style={{ width: "80px" }}>Sr. No.</th>
                                        <th style={{ width: "100px" }}>Image</th>
                                        <th onClick={() => requestSort('hotel_name')} style={{ cursor: 'pointer' }}>Hotel Name{renderSortArrow('hotel_name')}</th>
                                        <th onClick={() => requestSort('hotel_price')} style={{ cursor: 'pointer' }}>Package Price{renderSortArrow('hotel_price')}</th>
                                        <th onClick={() => requestSort('offer_price')} style={{ cursor: 'pointer' }}>Offer Price{renderSortArrow('offer_price')}</th>
                                        <th onClick={() => requestSort('city')} style={{ cursor: 'pointer' }}>City{renderSortArrow('city')}</th>
                                        <th onClick={() => requestSort('status')} style={{ cursor: 'pointer' }}>Status{renderSortArrow('status')}</th>
                                        <th onClick={() => requestSort('created_at')} style={{ cursor: 'pointer' }}>Created By{renderSortArrow('vendor.name')}</th>
                                        <th>Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {loading ? (
                                        <tr><td colSpan="8" className="text-center">Loading...</td></tr>
                                    ) : paginatedData.length > 0 ? (
                                        paginatedData.map((hotel, index) => (
                                            <tr key={hotel.id}>
                                                <td>{(currentPage - 1) * pageSize + index + 1}</td>
                                                <td>
                                                    {hotel.Images && hotel.Images.length > 0 ? (
                                                        <img
                                                            src={hotel.Images[0].image_url}
                                                            alt={hotel.hotel_name}
                                                            style={{ width: '60px', height: '60px', objectFit: 'cover', borderRadius: '4px' }}
                                                        />
                                                    ) : (
                                                        <span className="text-muted">No Image</span>
                                                    )}
                                                </td>
                                                <td>
                                                    <Link to={`/admin/edit-hotel/${hotel.id}`}>
                                                        {hotel.hotel_name}
                                                    </Link>
                                                </td>
                                                 <td>₹{formatPrice(hotel.hotel_price)}</td>
                                                 <td>₹{formatPrice(hotel.offer_price)}</td>
                                                <td>{hotel.city}</td>
                                                <td>
                                                    {renderStatusBadge(hotel.status, hotel.rejection_reason)}
                                                </td>
                                                <td>
                                                    {formatDate(hotel.created_at)}
                                                </td>
                                                <td>
                                                    <button onClick={() => handleDelete(hotel.id)} className="btn btn-sm btn-outline-danger" title="Delete">
                                                        <i className="ri-delete-bin-line"></i>
                                                    </button>
                                                </td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr><td colSpan="9" className="text-center">No hotels found.</td></tr>
                                    )}
                                </tbody>
                            </table>
                        </div>

                        {/* Pagination controls remain functional */}
                        <div className="d-flex justify-content-between align-items-center mt-3 flex-wrap">
                            <div className="mb-2">
                                {filteredData.length > 0 && (
                                    <span>
                                        Showing {(currentPage - 1) * pageSize + 1} to{" "}
                                        {Math.min(currentPage * pageSize, filteredData.length)} of {filteredData.length} results
                                    </span>
                                )}
                            </div>
                            <div>
                                <ul className="pagination pagination-sm mb-0">
                                    <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                                        <button className="page-link" onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}>Previous</button>
                                    </li>
                                    {Array.from({ length: totalPages }, (_, i) => (
                                        <li key={i} className={`page-item ${currentPage === i + 1 ? 'active' : ''}`}>
                                            <button className="page-link" onClick={() => setCurrentPage(i + 1)}>{i + 1}</button>
                                        </li>
                                    ))}
                                    <li className={`page-item ${currentPage === totalPages || totalPages === 0 ? 'disabled' : ''}`}>
                                        <button className="page-link" onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}>Next</button>
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default ListingHotels;
